import {
  fetchAdminDeploys,
  fetchAdminLambdas,
  fetchAdminVisitors,
  type AdminRequest,
} from "@jkast90/kast-common/admin-core";
import { apiClient, type RequestOptions } from "./api";
import type { Location } from "./impactService";
import type { Order, ShopVariantGroup } from "./shopService";

export interface Submission {
  submission_id: string;
  status: "pending" | "approved" | "rejected";
  location_id?: string;
  new_location_name?: string;
  new_location_lat?: number;
  new_location_lng?: number;
  new_location_description?: string;
  event_date: string;
  bags_collected: number;
  volunteer_count: number;
  notes?: string;
  submitter_name?: string;
  submitter_email?: string;
  kennel?: string;
  created_at: string;
  photos?: Array<{
    photo_id: string;
    public_url: string;
    caption: string;
    status: "pending" | "approved" | "rejected";
  }>;
  reviewed_at?: string;
  reviewed_by?: string;
  reject_reason?: string;
}

export interface AdminStatusCounts {
  pending: number;
  approved: number;
  rejected: number;
  total: number;
}

export interface AdminStats {
  submissions: AdminStatusCounts;
  photos: AdminStatusCounts;
  locations: { total: number };
  // Optional: the deployed API may not include these yet. Client falls back to 0.
  events?: { total: number };
  kennels?: { total: number };
  orders?: { total: number };
}

export interface AdminShopItem {
  item_id: string;
  name: string;
  description: string;
  price_cents: number;
  currency: string;
  is_active: boolean;
  in_stock: boolean; // derived server-side from stock_quantity > 0
  stock_quantity: number;
  stock_owner_email: string;
  stock_owner_name: string;
  photo_s3_key?: string;
  photo_content_type?: string;
  photo_public_url: string;
  variants: ShopVariantGroup[];
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface AdminStockOwner {
  email: string;
  name: string;
}

export interface AdminCognitoUser {
  email: string;
  name: string;
  found: boolean;
}

export interface AdminOrder extends Order {
  customer_phone?: string;
  customer_notes?: string;
  admin_notes?: string;
  paid_at?: string;
  shipped_at?: string;
  cancelled_at?: string;
  status_changed_by?: string;
  updated_at?: string;
  // Server returns raw dynamo row; flatten shipping fields are also available
  // directly for cards that don't need the nested address.
  ship_line1?: string;
  ship_line2?: string;
  ship_city?: string;
  ship_region?: string;
  ship_postal_code?: string;
  ship_country?: string;
}

export interface ShopUploadUrlResponse {
  upload_url: string;
  s3_key: string;
  public_url: string;
}

export interface AdminSettings {
  shop_enabled: boolean;
  paypal_handle: string;
  venmo_handle: string;
}

export interface AdminSummary {
  totals: {
    submissions: AdminStatusCounts;
    photos: AdminStatusCounts;
    orders: {
      pending_payment: number;
      paid: number;
      shipped: number;
      cancelled: number;
      total: number;
    };
    locations: number;
    kennels: number;
    events: number;
    bags: number;
    volunteers: number;
    visitors_unique_ips: number;
  };
  recent_30d: {
    submissions: number;
    photos: number;
    orders: number;
    visitors_active: number;
  };
  shop: {
    hot_items: Array<{
      item_id: string;
      name: string;
      units_sold: number;
      revenue_cents: number;
    }>;
    revenue_cents_30d: number;
    revenue_cents_all: number;
  };
  top_kennels_by_bags: Array<{
    kennel: string;
    total_bags: number;
    total_events: number;
    total_volunteers: number;
  }>;
  top_kennels_by_events: Array<{
    kennel: string;
    total_bags: number;
    total_events: number;
    total_volunteers: number;
  }>;
  top_visitors: AdminVisitor[];
  recent_orders: Array<{
    order_id: string;
    status: string;
    customer_name: string;
    total_cents: number;
    created_at: string;
  }>;
}

/** The app's client as the shared admin fetchers take it (`adminQueries`, the members changes). */
export const adminRequest: AdminRequest = (path, init) => apiClient.request(path, init as RequestOptions);

export const adminService = {
  getStats: () => apiClient.get<AdminStats>("/admin/stats"),

  getSummary: () => apiClient.get<AdminSummary>("/admin/summary"),

  listSubmissions: (submission_status = "all") =>
    apiClient.get<Submission[]>("/admin/submissions", { submission_status }),

  approveSubmission: (id: string, opts: { location_id?: string; create_location?: boolean } = {}) =>
    apiClient.post<Submission>(`/admin/submissions/${id}/approve`, opts),

  rejectSubmission: (id: string, reason: string) =>
    apiClient.post<Submission>(`/admin/submissions/${id}/reject`, { reason }),

  updateSubmission: (
    id: string,
    updates: Partial<{
      event_date: string;
      bags_collected: number;
      volunteer_count: number;
      notes: string;
      submitter_name: string;
      submitter_email: string;
      kennel: string;
      location_id: string;
      new_location_name: string;
      new_location_lat: number;
      new_location_lng: number;
      new_location_description: string;
      status: "pending" | "approved" | "rejected";
      reject_reason: string;
    }>,
  ) => apiClient.patch<Submission>(`/admin/submissions/${id}`, updates),

  deleteSubmission: (id: string) => apiClient.delete<void>(`/admin/submissions/${id}`),

  listLocations: () => apiClient.get<Location[]>("/admin/locations"),

  createLocation: (payload: { name: string; lat: number; lng: number; description?: string }) =>
    apiClient.post<Location>("/admin/locations", payload),

  updateLocation: (id: string, updates: Partial<{ name: string; lat: number; lng: number; description: string }>) =>
    apiClient.patch<Location>(`/admin/locations/${id}`, updates),

  deleteLocation: (id: string) => apiClient.delete<void>(`/admin/locations/${id}`),

  // Shop items
  listShopItems: () => apiClient.get<AdminShopItem[]>("/admin/shop/items"),

  createShopItem: (payload: {
    name: string;
    description?: string;
    price_cents: number;
    is_active?: boolean;
    stock_quantity?: number;
    stock_owner_email?: string;
    stock_owner_name?: string;
    photo_s3_key?: string;
    photo_content_type?: string;
    variants?: ShopVariantGroup[];
    sort_order?: number;
  }) => apiClient.post<AdminShopItem>("/admin/shop/items", payload),

  updateShopItem: (
    id: string,
    updates: Partial<{
      name: string;
      description: string;
      price_cents: number;
      is_active: boolean;
      stock_quantity: number;
      stock_owner_email: string;
      stock_owner_name: string;
      photo_s3_key: string;
      photo_content_type: string;
      variants: ShopVariantGroup[];
      sort_order: number;
    }>,
  ) => apiClient.patch<AdminShopItem>(`/admin/shop/items/${id}`, updates),

  listStockOwners: () => apiClient.get<AdminStockOwner[]>("/admin/shop/stock-owners"),

  lookupCognitoUser: (email: string, signal?: AbortSignal) =>
    apiClient.get<AdminCognitoUser>("/admin/cognito/lookup", { email }, signal),

  // Settings
  getSettings: () => apiClient.get<AdminSettings>("/admin/settings"),
  updateSettings: (updates: Partial<AdminSettings>) => apiClient.patch<AdminSettings>("/admin/settings", updates),

  deleteShopItem: (id: string) => apiClient.delete<void>(`/admin/shop/items/${id}`),

  requestShopItemUploadUrl: (content_type: string) =>
    apiClient.post<ShopUploadUrlResponse>("/admin/shop/items/upload-url", {
      content_type,
    }),

  // Orders
  listOrders: (order_status: string = "all", limit: number = 100) =>
    apiClient.get<AdminOrder[]>("/admin/orders", { order_status, limit }),

  getOrder: (id: string) => apiClient.get<AdminOrder>(`/admin/orders/${id}`),

  updateOrder: (
    id: string,
    updates: Partial<{
      status: "pending_payment" | "paid" | "shipped" | "cancelled";
      admin_notes: string;
    }>,
  ) => apiClient.patch<AdminOrder>(`/admin/orders/${id}`, updates),

  // The shared admin endpoints, through the package's fetchers (the tabs'
  // tests stub these; queries.ts keys them with the package's adminQueryKeys).
  // Visitors
  listVisitors: () => fetchAdminVisitors(adminRequest),

  // Deploys (served by api; see terraform/hnt_api_deploys.tf)
  listDeploys: () => fetchAdminDeploys(adminRequest),

  // Lambdas (served by api; see terraform/hnt_api_lambdas.tf)
  listLambdas: () => fetchAdminLambdas(adminRequest),
};

/** A row of the central visitor log (newest first; null when unknown). */
export interface AdminVisitor {
  ip: string;
  count: number;
  first_seen: string | null;
  last_seen: string | null;
}
